import {
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { LelyIconName } from '../buttonTypes';
import {
  LelyFieldHelperText,
  LelyFieldSharedProps,
  LelyFieldUnitAdornment,
  shouldShowLelyFieldUnitText,
} from '../common/lelyFieldShared';
import LelyIconButton from '../LelyIconButton/LelyIconButton';

export type LelyTextFieldProps = LelyFieldSharedProps & {
  errorMessage?: string;
  icon?: LelyIconName;
} & Omit<TextFieldProps, 'helperText'>;

function getInputSlotObject(slotProps: TextFieldProps['slotProps']) {
  return slotProps?.input && typeof slotProps.input === 'object'
    ? (slotProps.input as { endAdornment?: ReactNode })
    : undefined;
}

function LelyTextField({
  errorMessage,
  showHelperText,
  helperText,
  valid = true,
  showUnitText,
  unitText,
  icon,
  error,
  InputProps,
  slotProps,
  sx,
  ...rest
}: LelyTextFieldProps) {
  const theme = useTheme();
  const helperTextNode =
    typeof helperText === 'boolean' ? undefined : helperText;
  const isValid = valid && !errorMessage && !error;
  const resolvedHelperText = !isValid
    ? errorMessage || helperTextNode
    : helperTextNode;
  const shouldShowUnitText = shouldShowLelyFieldUnitText(
    showUnitText,
    unitText,
  );
  const inputSlotProps = getInputSlotObject(slotProps);
  const consumerEndAdornment =
    inputSlotProps?.endAdornment ?? InputProps?.endAdornment;
  const hasBuiltEndAdornment = shouldShowUnitText || Boolean(icon);
  const endAdornment = hasBuiltEndAdornment ? (
    <>
      {shouldShowUnitText && (
        <LelyFieldUnitAdornment
          unitText={unitText}
          sx={icon ? undefined : { marginRight: theme.lelySpacing.MD }}
        />
      )}
      {icon && (
        <InputAdornment
          position="end"
          sx={{ marginRight: theme.lelySpacing.MD }}
        >
          <LelyIconButton
            icon={icon}
            size="lg"
            tabIndex={-1}
            aria-hidden
            disableRipple
            sx={{
              padding: 0,
              color: theme.palette.text.subtle,
              pointerEvents: 'none',
            }}
          />
        </InputAdornment>
      )}
      {consumerEndAdornment}
    </>
  ) : (
    consumerEndAdornment
  );

  return (
    <Stack>
      <TextField
        sx={sx}
        {...rest}
        variant="filled"
        fullWidth
        error={!isValid}
        slotProps={{
          ...slotProps,
          input: {
            ...InputProps,
            ...inputSlotProps,
            endAdornment,
          },
        }}
      />
      <LelyFieldHelperText
        showHelperText={showHelperText}
        helperText={resolvedHelperText}
        valid={isValid}
      />
    </Stack>
  );
}

export default LelyTextField;
